import React, { useState, useEffect } from 'react';
import api from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import '../../styles/ListPage.css';

function CompanyList() {
    const navigate = useNavigate();
    const { isLoading, withLoading } = usePageLoading(true);
    const [companies, setCompanies] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredCompanies, setFilteredCompanies] = useState([]);

    useEffect(() => {
        withLoading(async () => {
            const res = await api.get('/companies');
            setCompanies(res.data);
            setFilteredCompanies(res.data);
        }).catch((err) => console.error('Error fetching companies:', err));
    }, [withLoading]);

    useEffect(() => {
        const results = companies.filter(company =>
            (company.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (company.gstNo || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredCompanies(results);
    }, [searchTerm, companies]);

    const handleCheckboxChange = (companyId) => {
        setSelectedCompanies(prev =>
            prev.includes(companyId) ? prev.filter(id => id !== companyId) : [...prev, companyId]
        );
    };

    const handleSelectAll = (e) => {
        setSelectedCompanies(e.target.checked ? filteredCompanies.map(company => company.id) : []);
    };

    const handleDeleteSelected = () => {
        if (!window.confirm('Are you sure you want to delete the selected companies?')) return;
        Promise.all(selectedCompanies.map(companyId => api.delete(`/companies/${companyId}`)))
            .then(() => {
                setCompanies(prev => prev.filter(company => !selectedCompanies.includes(company.id)));
                setSelectedCompanies([]);
            })
            .catch(() => {
                alert('Error deleting companies');
            });
    };

    const allSelected = filteredCompanies.length > 0 && selectedCompanies.length === filteredCompanies.length;

    return (
        <PageLoadingArea isLoading={isLoading}>
        <div className="list-page">
            <div className="list-page-header">
                <div className="list-page-title">
                    <h1>Companies</h1>
                    <p className="list-page-subtitle">
                        {filteredCompanies.length} compan{filteredCompanies.length === 1 ? 'y' : 'ies'}
                    </p>
                </div>
                <div className="list-header-actions">
                    <div className="list-search-wrap">
                        <input
                            type="text"
                            placeholder="Search company name or GST"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="list-search-input"
                        />
                    </div>
                    <div className="list-button-group">
                        <button
                            type="button"
                            onClick={() => navigate('/admin')}
                            className="list-secondary-button"
                        >
                            Back to admin
                        </button>
                        <button type="button" onClick={() => navigate('/createCompany')} className="list-add-button">
                            Add New Company
                        </button>
                        {selectedCompanies.length > 0 && (
                            <button type="button" onClick={handleDeleteSelected} className="list-delete-button">
                                Delete ({selectedCompanies.length})
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="list-table-card">
                <div className="list-table-scroll">
                    <table className="list-table">
                        <thead>
                            <tr>
                                <th className="col-check">
                                    <input type="checkbox" checked={allSelected} onChange={handleSelectAll} aria-label="Select all" />
                                </th>
                                <th>Sr No</th>
                                <th>Company Name</th>
                                <th>GST No</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCompanies.length === 0 ? (
                                <tr className="list-empty-row">
                                    <td colSpan="4">
                                        <div className="list-empty-state">
                                            <h3>No companies found</h3>
                                            <p>{searchTerm ? 'Try a different search term.' : 'Add your first company to get started.'}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredCompanies.map((company, index) => (
                                <tr
                                    key={company.id}
                                    className={`list-row${selectedCompanies.includes(company.id) ? ' is-selected' : ''}`}
                                    onClick={(e) => {
                                        if (e.target.type !== 'checkbox') navigate(`/createCompany/${company.id}`);
                                    }}
                                >
                                    <td className="col-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedCompanies.includes(company.id)}
                                            onChange={() => handleCheckboxChange(company.id)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td><span className="list-primary-text">{company.name}</span></td>
                                    <td>{company.gstNo || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        </PageLoadingArea>
    );
}

export default CompanyList;
